/**
 * ComplianceTab — "Indice di conformità" del negozio: ultimo indice %, trend
 * vs controllo precedente e storico delle ispezioni. Riservato a OPS e AM.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { complianceApi } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ClipboardList, Loader2, Plus, TrendingDown, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import ComplianceCheckDetail from './ComplianceCheckDetail';
import ComplianceTemplateDialog from './ComplianceTemplateDialog';

export const pctColor = (p) => (p >= 90 ? 'text-emerald-600' : p >= 70 ? 'text-amber-600' : 'text-red-600');

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

export default function ComplianceTab({ storeId, storeName }) {
  const { user } = useAuth();
  const isOps = user?.role === 'operations_manager';
  const [checks, setChecks] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);

  const load = useCallback(() => {
    complianceApi.checks(storeId)
      .then(r => setChecks(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Errore caricamento controlli'));
  }, [storeId]);

  useEffect(() => { load(); }, [load]);

  const newCheck = async () => {
    setCreating(true);
    try {
      const r = await complianceApi.createCheck({ store_id: storeId });
      setOpenId(r.data.check_id);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Creazione fallita');
    } finally {
      setCreating(false);
    }
  };

  if (!checks) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  const last = checks[0];
  const prev = checks[1];
  const delta = last && prev ? Math.round((last.percent - prev.percent) * 10) / 10 : null;

  return (
    <div className="space-y-4" data-testid="compliance-tab">
      <div className="flex flex-wrap items-stretch gap-3">
        <div className="bg-slate-900 text-white p-4 min-w-[220px]" data-testid="compliance-summary">
          <p className="text-[10px] uppercase tracking-wider text-slate-400">Indice di conformità</p>
          {last ? (
            <>
              <p className="text-3xl font-bold tabular-nums" data-testid="compliance-last-percent">
                {last.percent}<span className="text-lg text-slate-400">%</span>
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Ultimo controllo: {fmtDate(last.date)}
                {delta != null && (
                  <span className={`ml-2 inline-flex items-center gap-0.5 ${delta >= 0 ? 'text-emerald-400' : 'text-red-400'}`}
                        data-testid="compliance-delta">
                    {delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {delta > 0 ? '+' : ''}{delta} pt
                  </span>
                )}
              </p>
            </>
          ) : (
            <p className="text-sm text-slate-300 mt-1">Nessun controllo ancora</p>
          )}
        </div>
        <div className="flex-1" />
        <div className="flex items-end gap-2 flex-wrap">
          {isOps && (
            <Button variant="outline" size="sm" className="h-9 gap-1.5 rounded-none" onClick={() => setShowTemplate(true)}
                    data-testid="compliance-template-btn">
              <ClipboardList className="w-4 h-4" /> Template
            </Button>
          )}
          <Button size="sm" className="h-9 gap-1.5 rounded-none" onClick={newCheck} disabled={creating}
                  data-testid="compliance-new-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Nuovo controllo
          </Button>
        </div>
      </div>

      {checks.length === 0 ? (
        <div className="border border-dashed border-slate-300 py-12 text-center text-slate-400">
          <p className="text-sm">Nessun controllo di conformità per questo negozio. Avvia il primo!</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-3 py-2">Data</th>
                <th className="px-3 py-2">Indice</th>
                <th className="px-3 py-2 hidden sm:table-cell">Valutatore</th>
                <th className="px-3 py-2 hidden sm:table-cell">Referente</th>
                <th className="px-3 py-2">Stato</th>
                <th className="px-3 py-2 hidden md:table-cell">Firme</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {checks.map(c => (
                <tr key={c.check_id} className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setOpenId(c.check_id)} data-testid={`compliance-row-${c.check_id}`}>
                  <td className="px-3 py-2.5 whitespace-nowrap">{fmtDate(c.date)}</td>
                  <td className={`px-3 py-2.5 font-bold tabular-nums ${pctColor(c.percent)}`}>{c.percent}%</td>
                  <td className="px-3 py-2.5 hidden sm:table-cell text-slate-600">{c.evaluator_name || '—'}</td>
                  <td className="px-3 py-2.5 hidden sm:table-cell text-slate-600">{c.referente_name || '—'}</td>
                  <td className="px-3 py-2.5">
                    <Badge variant="outline" className={c.status === 'final'
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-amber-50 text-amber-700 border-amber-200'}>
                      {c.status === 'final' ? 'Definitivo' : 'Bozza'}
                    </Badge>
                  </td>
                  <td className="px-3 py-2.5 hidden md:table-cell">
                    <span className="flex gap-1">
                      <Badge variant="outline" className={c.signatures?.evaluator ? 'bg-slate-900 text-white' : 'text-slate-400'}>V</Badge>
                      <Badge variant="outline" className={c.signatures?.referente ? 'bg-slate-900 text-white' : 'text-slate-400'}>R</Badge>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId && (
        <ComplianceCheckDetail checkId={openId} storeName={storeName}
                               onClose={() => { setOpenId(null); load(); }} />
      )}
      {showTemplate && <ComplianceTemplateDialog onClose={() => setShowTemplate(false)} />}
    </div>
  );
}
